import { fontStyles } from '@/styles/typography';

import { triggerFeedbackHint } from '@/utils/feedback-hint';

import { FeedbackAnimation } from './FeedbackAnimation';
import { ToastContainer, ToastTitle, ToastContent } from './ToastComponents';

export interface FeedbackToastProps {
  onClose?: () => void;
  onFeedbackClick: () => void;
  title?: string;
  description?: string;
}

const DEFAULT_TITLE = 'How can we improve Matcha?';
const DEFAULT_DESCRIPTION =
  'We’d love to hear your thoughts so we can shape Matcha to better fit your needs.';

/**
 * Generic feedback prompt toast. Used across surfaces (e.g. swap-success,
 * portfolio scroll). Does not auto-dismiss — user must manually close it.
 */
export function FeedbackToast({
  onClose,
  onFeedbackClick,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
}: FeedbackToastProps) {
  const handleClose = () => {
    triggerFeedbackHint();
    onClose?.();
  };

  const handleFeedbackClick = () => {
    onFeedbackClick();
  };

  return (
    <ToastContainer onClose={handleClose} showProgressAnimation={false} onHoverChange={() => {}}>
      <FeedbackAnimation feedback="feedback" className="flex-shrink-0 relative z-10" />

      <div className="flex flex-col gap-1 min-w-0 flex-1 relative z-10 pr-8">
        <ToastTitle
          className={fontStyles({
            variant: 'LABEL_MD',
            class: ['text-token-gray-1600'],
          })}
        >
          {title}
        </ToastTitle>

        <ToastContent>
          <span
            className={fontStyles({
              variant: 'BODY_SM',
              class: ['text-token-gray-800'],
            })}
          >
            {description}
          </span>
        </ToastContent>

        <div>
          <button
            onClick={handleFeedbackClick}
            className={fontStyles({
              variant: 'LABEL_MD',
              class: [
                'text-token-purple-900',
                'cursor-pointer',
                'p-1.5',
                '-ml-1',
                'rounded-[8px]',
                'hover:bg-token-purple-200',
                'active:bg-token-purple-200',
                'focus:outline-none',
                'focus-visible:ring-2',
                'focus-visible:ring-token-purple-500',
                'transition-all',
                'duration-150',
                'ease-in-out',
              ],
            })}
            type="button"
          >
            Give Feedback
          </button>
        </div>
      </div>
    </ToastContainer>
  );
}
